import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Loaded inside the Fire TV app's WebView (MainActivity.kt) when no auth
 * token is saved yet. The user is already signed into qudetv.com in this
 * WebView (ProtectedRoute below handles that the normal way -- Clerk's
 * hosted sign-in, typed via the TV remote). Once signed in, mint a
 * long-lived Qude detection token (same 7-day token the phone app upgrades
 * to -- a raw Clerk JWT is too short-lived for a background TV service that
 * doesn't get refreshed the way the phone's AndroidBridge.js does) and hand
 * it to the native shell via the qude://token/ scheme MainActivity's
 * WebViewClient.shouldOverrideUrlLoading intercepts.
 *
 * That interception PREVENTS navigation (it returns true), so this page
 * never actually unloads -- show a confirmation in place rather than
 * expecting a route change.
 */
export default function FireTVPair() {
  const { getToken } = useAuth();
  const [status, setStatus] = useState('pairing'); // pairing | paired | error

  useEffect(() => {
    const pair = async () => {
      try {
        const clerkToken = await getToken();
        const res = await axios.post(`${API}/auth/detection-token`, {}, {
          headers: { Authorization: `Bearer ${clerkToken}` },
        });
        const qudeToken = res?.data?.token;
        if (!qudeToken) throw new Error('No token minted');
        setStatus('paired');
        window.location.href = `qude://token/${qudeToken}`;
      } catch (e) {
        console.error('[Qude] Fire TV pairing failed:', e);
        setStatus('error');
      }
    };
    pair();
  }, [getToken]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#050508', flexDirection: 'column', gap: 20, padding: 24, textAlign: 'center',
    }}>
      {status === 'pairing' && (
        <>
          <div style={{ width: 40, height: 40, border: '3px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>Pairing this Fire TV with Qude…</p>
        </>
      )}
      {status === 'paired' && (
        <>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#22c55e,#4A9EFF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={30} style={{ color: '#fff' }} />
          </div>
          <p style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>Paired!</p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, maxWidth: 420 }}>
            This Fire TV will now automatically detect what you watch. Browse and launch shows from your phone or qudetv.com — you don't need to keep this screen open.
          </p>
        </>
      )}
      {status === 'error' && (
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }}>
          Couldn't pair this Fire TV. Check your connection and try again.
        </p>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
